import Constants from 'expo-constants';

import { getAuthorizationHeader } from '../auth/stytchHelpers';
import { API_PORT, API_URL } from '../config/config';
import { IS_LOCAL } from '../config/FruitfulEnvironment';
import { captureException, captureMessage } from '../logger/Logger';
import { fetchErrorFromResponseBody } from './errorResponseBody';

interface FetcherArgs {
    endpoint: string;
    body?: BodyInit;
    headers?: HeadersInit;
    /** @default "GET" */
    method?: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT';
}

const mergeRequestHeaders = (extra?: HeadersInit): Record<string, string> => {
    const base: Record<string, string> = {
        'Content-Type': 'application/json',
        ...getAuthorizationHeader(),
    };
    if (extra == null) {
        return base;
    }
    if (extra instanceof Headers) {
        const merged = { ...base };
        extra.forEach((value, key) => {
            merged[key] = value;
        });
        return merged;
    }
    if (Array.isArray(extra)) {
        return { ...base, ...Object.fromEntries(extra) };
    }
    return { ...base, ...extra };
};

const getApiHost = () => {
    // Allow for local development environment where the backend api is running on a different local network IP Address (e.g. making requests from iOS device to laptop all on same LAN).
    if (IS_LOCAL && Constants.expoConfig?.hostUri != null) {
        const hostIp = Constants.expoConfig.hostUri.split(`:`).shift();
        return `http://${hostIp}:${API_PORT ?? ''}`;
    } else {
        return API_URL;
    }
};

export const fetcher = async <Data = unknown>(args: FetcherArgs) => {
    const { endpoint, body, headers, method = 'GET' } = args;

    const url = `${getApiHost()}${endpoint}`;

    const requestData: RequestInit = {
        headers: mergeRequestHeaders(headers),
        method,
    };

    if (method !== 'GET') {
        requestData.body = body;
    }

    const response = await fetch(url, requestData);

    if (!response.ok) {
        // Read text first. Error pages from the ALB are HTML, and response.json()
        // throws SyntaxError before we can record the status.
        const bodyText = await response.text();
        throw fetchErrorFromResponseBody({
            bodyText,
            response,
            endpoint,
            method,
        });
    }

    if (response.status === 204) {
        return '' as Data;
    }

    // Read the body once. An empty body is a normal success for some writes,
    // including analytics posts, whether or not Content-Length is 0. Callers
    // that need a JSON object must reject that value themselves.
    const text = await response.text();
    if (text.trim() === '') {
        captureMessage(`Received empty response body from server ${url}`);
        return '' as Data;
    }

    try {
        const data: unknown = JSON.parse(text);
        return data as Data;
    } catch (error: unknown) {
        const detail = error instanceof Error ? error.message : 'invalid JSON';
        const parseError = new Error(
            `Received an invalid response body from ${url}: ${detail}`,
        );
        captureException(parseError);
        throw parseError;
    }
};

export interface SearchResult<T> {
    total: number;
    results: T[];
}
